/**
 * Landing Scene - Premium first impression
 *
 * Design principles:
 * - Confident, deliberate pacing
 * - Zero visual clutter
 * - Calm authority
 * - Clear focus
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { BaseScene, type SceneContext } from './BaseScene';
import { Button } from '@ui/Button';
import { theme } from '@core/theme';
import { app } from '@core/Application';

export class LandingScene extends BaseScene {
  private content!: Container;
  private startButton!: Button;
  private vignetteOverlay!: Graphics;

  constructor(context: SceneContext) {
    super(context);
  }

  async init(): Promise<void> {
    // Background with subtle gradient
    const bg = new Graphics();
    this.addChild(bg);
    this.drawBackground(bg);

    // Ambient road lines (very subtle, adds life)
    this.createAmbientBackground();

    // Vignette for focus
    this.vignetteOverlay = new Graphics();
    this.addChild(this.vignetteOverlay);
    this.drawVignette();

    // Content container
    this.content = new Container();
    this.content.alpha = 0; // Start hidden for entrance animation
    this.addChild(this.content);

    // Logo mark (abstract road symbol)
    const logoMark = this.createLogoMark();
    logoMark.position.set(0, -40);
    this.content.addChild(logoMark);

    // Title - institutional, authoritative
    const title = new Text({
      text: 'SafePassage',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes['5xl'],
        fontWeight: theme.typography.weights.bold,
        fill: theme.colors.textPrimary,
        letterSpacing: theme.typography.letterSpacing.tight,
      }),
    });
    title.anchor.set(0.5, 0);
    title.position.set(0, 20);
    this.content.addChild(title);

    // Subtitle with accent
    const subtitle = new Text({
      text: 'ROAD SAFETY SIMULATION',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.xs,
        fontWeight: theme.typography.weights.semibold,
        fill: theme.colors.decision,
        letterSpacing: 4,
      }),
    });
    subtitle.anchor.set(0.5, 0);
    subtitle.position.set(0, 80);
    this.content.addChild(subtitle);

    // Horizontal rule
    const rule = new Graphics();
    rule.rect(-40, 0, 80, 1);
    rule.fill({ color: theme.colors.neutral, alpha: 0.4 });
    rule.position.set(0, 120);
    this.content.addChild(rule);

    // Description - clear, confident
    const description = new Text({
      text: 'Practice making safe decisions in realistic traffic situations.\nLearn to recognize hazards and anticipate consequences.',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.base,
        fill: theme.colors.textSecondary,
        wordWrap: true,
        wordWrapWidth: 440,
        align: 'center',
        lineHeight: theme.typography.sizes.base * theme.typography.lineHeight.relaxed,
      }),
    });
    description.anchor.set(0.5, 0);
    description.position.set(0, 150);
    this.content.addChild(description);

    // Feature badges
    const features = [
      { icon: '○', text: 'No account required' },
      { icon: '◷', text: '15-25 minute sessions' },
      { icon: '↻', text: 'Learn from every decision' },
    ];

    const featuresContainer = new Container();
    featuresContainer.position.set(0, 250);
    this.content.addChild(featuresContainer);

    features.forEach((feature, i) => {
      const featureContainer = new Container();
      const xOffset = (i - 1) * 160;
      featureContainer.position.set(xOffset, 0);

      // Icon circle
      const iconBg = new Graphics();
      iconBg.circle(0, 0, 16);
      iconBg.fill({ color: theme.colors.safe, alpha: 0.15 });
      iconBg.circle(0, 0, 16);
      iconBg.stroke({ width: 1, color: theme.colors.safe, alpha: 0.3 });
      featureContainer.addChild(iconBg);

      // Checkmark
      const check = new Graphics();
      check.moveTo(-5, 0);
      check.lineTo(-2, 4);
      check.lineTo(5, -4);
      check.stroke({ width: 2, color: theme.colors.safe });
      featureContainer.addChild(check);

      // Text
      const featureText = new Text({
        text: feature.text,
        style: new TextStyle({
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.sizes.xs,
          fill: theme.colors.textMuted,
        }),
      });
      featureText.anchor.set(0.5, 0);
      featureText.position.set(0, 24);
      featureContainer.addChild(featureText);

      featuresContainer.addChild(featureContainer);
    });

    // Start button - prominent, inviting
    this.startButton = new Button({
      label: 'Begin Training',
      width: 200,
      height: 52,
      variant: 'primary',
      size: 'lg',
    });
    this.startButton.position.set(-100, 340);
    this.startButton.setOnClick(() => this.handleStart());
    this.content.addChild(this.startButton);

    // Keyboard hint
    const keyHint = new Text({
      text: 'Press ENTER to start',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.xs,
        fill: theme.colors.textMuted,
        letterSpacing: 1,
      }),
    });
    keyHint.anchor.set(0.5, 0);
    keyHint.position.set(0, 405);
    this.content.addChild(keyHint);

    // Position and animate
    this.positionContent();
    this.startAnimations();
    this.setupKeyboard();
  }

  /**
   * Create subtle logo mark
   */
  private createLogoMark(): Container {
    const mark = new Container();

    // Road/crossing symbol
    const g = new Graphics();

    // Horizontal road
    g.roundRect(-24, -3, 48, 6, 2);
    g.fill({ color: theme.colors.decision, alpha: 0.9 });

    // Vertical road
    g.roundRect(-3, -24, 6, 48, 2);
    g.fill({ color: theme.colors.decision, alpha: 0.9 });

    // Center dot
    g.circle(0, 0, 4);
    g.fill(theme.colors.textPrimary);

    mark.addChild(g);
    return mark;
  }

  /**
   * Draw simple background
   */
  private drawBackground(g: Graphics): void {
    g.clear();
    g.rect(0, 0, this.context.width, this.context.height);
    g.fill(theme.colors.primaryBg);
  }

  /**
   * Create static background pattern (no animation)
   */
  private createAmbientBackground(): void {
    // Static subtle pattern - no animation for performance
    const pattern = new Graphics();
    pattern.alpha = 0.02;

    // Simple horizontal lines
    for (let y = 0; y < this.context.height; y += 60) {
      pattern.rect(0, y, this.context.width, 2);
      pattern.fill(theme.colors.roadMarking);
    }

    this.addChildAt(pattern, 1);
  }

  /**
   * Draw simple vignette overlay
   */
  private drawVignette(): void {
    this.vignetteOverlay.clear();

    // Simple edge darkening - no complex gradients
    const edgeSize = 100;
    const alpha = 0.2;

    // Corner shadows only
    this.vignetteOverlay.rect(0, 0, edgeSize, edgeSize);
    this.vignetteOverlay.fill({ color: 0x000000, alpha });

    this.vignetteOverlay.rect(this.context.width - edgeSize, 0, edgeSize, edgeSize);
    this.vignetteOverlay.fill({ color: 0x000000, alpha });

    this.vignetteOverlay.rect(0, this.context.height - edgeSize, edgeSize, edgeSize);
    this.vignetteOverlay.fill({ color: 0x000000, alpha });

    this.vignetteOverlay.rect(this.context.width - edgeSize, this.context.height - edgeSize, edgeSize, edgeSize);
    this.vignetteOverlay.fill({ color: 0x000000, alpha });
  }

  /**
   * Start entrance animation only (no continuous animations)
   */
  private startAnimations(): void {
    // Simple fade in
    this.content.alpha = 0;

    const entranceStart = performance.now();
    const entranceDuration = 600;

    const animateEntrance = (time: number) => {
      const elapsed = time - entranceStart;
      const progress = Math.min(elapsed / entranceDuration, 1);
      const eased = theme.easings.easeOut(progress);

      this.content.alpha = eased;

      if (progress < 1) {
        requestAnimationFrame(animateEntrance);
      }
    };

    requestAnimationFrame(animateEntrance);
  }

  private setupKeyboard(): void {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && this.isActive) {
        this.handleStart();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    this.on('destroyed', () => {
      window.removeEventListener('keydown', handleKeydown);
    });
  }

  private handleStart(): void {
    app.navigateTo('role-select');
  }

  private positionContent(): void {
    this.content.position.set(
      this.context.width / 2,
      this.context.height / 2 - 180
    );
  }

  protected handleResize(): void {
    const bg = this.children[0] as Graphics;
    this.drawBackground(bg);
    this.drawVignette();
    this.positionContent();
  }

  destroy(): void {
    super.destroy();
  }
}
